import * as React from 'react';
import {connect} from 'react-redux';

import _ from 'underscore';

import {SavedFilters} from 'nutshell-core/saved-filters';
import {Session} from 'nutshell-core/session';
import type {SavedFilter, SavedReportType, SavedReportId} from 'nutshell-core/types';

import type {NutshellState} from '../../store';

import {SavedFilterModal} from '../base/saved-filter-modal/saved-filter-modal';
import type {
    ShareTypeJustMine,
    ShareTypeShowAll,
} from '../base/saved-filter-modal/saved-filter-modal';

import {getReportKeyFromReportSavedType, getReportTitle} from './report-constants';
import {ReportEmptyView} from './report-empty-view';
import {SavedReportsRow} from './saved-reports-row';

type OwnProps = {
    isOpen: boolean;
    onClose: () => any;
    preselectedTab?: ShareTypeJustMine | ShareTypeShowAll;
};
type StateProps = {
    savedReports: Partial<Record<SavedReportId, SavedFilter<SavedReportType>>>;
    loadingErrorMessage?: string;
    actionErrorMessage?: string;
    isLoading: boolean;
    sessionUserId: string | null | undefined;
};
type DispatchProps = {
    fetchSavedReports: () => any;
    onDelete: (report: SavedFilter<SavedReportType>) => any;
    onActionErrorHidden: () => any;
};
type Props = OwnProps & StateProps & DispatchProps;

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        fetchSavedReports: () => {
            dispatch(SavedFilters.fetchSavedReports());
        },
        onDelete: (report) => {
            dispatch(SavedFilters.deleteSavedFilter(report));
        },
        onActionErrorHidden: () => {
            dispatch(SavedFilters.clearActionError());
        },
    };
}

const mapStateToProps = (state: NutshellState): StateProps => {
    // Filter out hidden reports (AI-generated) from the modal
    const allReports = SavedFilters.getSavedReports(state);
    const visibleReports: Record<string, any> = {};
    Object.keys(allReports).forEach((id) => {
        if (!allReports[id].isHidden) {
            visibleReports[id] = allReports[id];
        }
    });

    return {
        savedReports: visibleReports,
        loadingErrorMessage: SavedFilters.getLoadingErrorMessage(state),
        actionErrorMessage: SavedFilters.getActionErrorMessage(state),
        isLoading: SavedFilters.getIsLoading(state),
        sessionUserId: Session.getSessionStateUserId(state),
    };
};

export class SavedReportsModalComponent extends React.Component<Props> {
    render() {
        return (
            <SavedFilterModal
                preselectedTab={this.props.preselectedTab}
                headerText='Saved reports'
                searchPlaceholder='Search for a report…'
                getExtraSearchableText={getTitle}
                // @ts-expect-error
                savedFilters={_.values(this.props.savedReports)}
                isOpen={this.props.isOpen}
                onClose={this.props.onClose}
                isLoading={this.props.isLoading}
                isErrored={Boolean(this.props.loadingErrorMessage)}
                errorMessage={this.props.actionErrorMessage}
                onErrorHidden={this.props.onActionErrorHidden}
                sessionUserId={this.props.sessionUserId}
                renderFilterRow={this.renderReportRow}
                renderEmptyView={this.renderEmptyView}
                renderNoPersonalSavedFilters={this.renderNoPersonalSavedReports}
            />
        );
    }

    renderNoPersonalSavedReports = () => {
        return (
            <ReportEmptyView
                isLoading={this.props.isLoading}
                onErrorRetry={this.handleErrorRetry}
                hasError={Boolean(this.props.loadingErrorMessage)}
                emptyText={"You haven't saved any reports yet"}
            />
        );
    };

    renderEmptyView = () => {
        // If there are savedReports, it means that the user has searched,
        // and none were found
        const emptyText = Object.keys(this.props.savedReports).length
            ? 'Couldn’t find any saved reports.'
            : 'No saved reports.';

        return (
            <ReportEmptyView
                isLoading={this.props.isLoading}
                onErrorRetry={this.handleErrorRetry}
                hasError={Boolean(this.props.loadingErrorMessage)}
                emptyText={emptyText}
            />
        );
    };

    renderReportRow = (report: SavedFilter<SavedReportType>, isSelected: boolean) => {
        return (
            <SavedReportsRow
                key={report.id}
                report={report}
                isSelected={isSelected}
                onSelect={this.props.onClose}
                onDelete={this.handleDelete}
                sessionUserId={this.props.sessionUserId}
            />
        );
    };

    handleErrorRetry = () => {
        this.props.fetchSavedReports();
    };

    handleDelete = (savedReportId: SavedReportId) => {
        const savedReport = this.props.savedReports[savedReportId];
        // @ts-expect-error
        this.props.onDelete(savedReport);
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);

export const SavedReportsModal = connector(SavedReportsModalComponent);

// @ts-expect-error
const getTitle = (report): string => {
    const reportKey = getReportKeyFromReportSavedType(report.reportType);
    const reportTitle = getReportTitle(reportKey);

    return reportTitle;
};
